import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { authService } from '@/services/AuthService';
import { AUTH_BFF_ENABLED } from '@/config/authBff';

const INACTIVITY_MS = 45 * 60 * 1000; // 45 minutes
// While active, slide the server-side session window at most this often.
const SLIDE_THROTTLE_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'] as const;

/**
 * Signs the user out after 45 minutes of no interaction, requiring re-auth.
 *
 * User activity resets the timer and (in BFF mode, throttled) slides the
 * server-side session cookie window so an active user is never logged out; the
 * server cookie also expires after 45 idle minutes as a backstop.
 */
export const useInactivityTimeout = (): void => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSlideRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') return;

    const reset = (): void => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        logout({});
      }, INACTIVITY_MS);
    };

    const onActivity = (): void => {
      reset();
      if (AUTH_BFF_ENABLED) {
        const now = Date.now();
        if (now - lastSlideRef.current >= SLIDE_THROTTLE_MS) {
          lastSlideRef.current = now;
          void authService.getBffSession();
        }
      }
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, logout]);
};
